import { type ReactNode, useMemo } from 'react';
import { Link, Text, type TextProps } from '@direct-frontend/components';
import { type Page } from '#shared/api/schema';
import { SCROLL_TO_ELEMENTS_MAP } from '#shared/lib/hocs';
import {
    constructorRoute,
    promoactionsRoute,
    SETTINGS_INTEGRATIONS_SEARCH_PARAMS_CONNECTED,
    settingsEmailNotificationsRoute,
    settingsIntegrationsRoute,
} from '#shared/lib/routes';
import { constructorTestIds } from '#shared/lib/testids';
import {
    CONSTRUCTOR_ACCOUNT_LOYALTY_ALIAS,
    CONSTRUCTOR_FAVOURITE_PRODUCTS_ALIAS,
    CONSTRUCTOR_GIFT_CARD_ALIAS,
    CONSTRUCTOR_GIFT_CARD_QUESTIONS_ALIAS,
    CONSTRUCTOR_LOYALTY_LANDING_ALIAS,
    CONSTRUCTOR_LOYALTY_OFFER_ALIAS,
    CONSTRUCTOR_PRODUCTS_BUNDLE_ALIAS,
    CONSTRUCTOR_SUBSCRIPTION_ALIAS,
} from '../model/constants';

const FavouritePublishedDescription = ({
    textSize,
    textRole,
}: {
    textSize: TextProps['size'];
    textRole: TextProps['role'];
}) => (
    <Text size={textSize} role={textRole}>
        Сейчас настройка отображается. Управлять настройками можно в разделе{' '}
        <Link
            data-testid={constructorTestIds.pageHideDescriptionConstructorThemeLink}
            href={constructorRoute.getUrl({
                searchParams: {
                    tab: 'theme',
                },
            })}
        >
            Тема&nbsp;→ Дополнительные возможности
        </Link>
        .
    </Text>
);

const FavouriteHiddenDescription = ({
    textSize,
    textRole,
}: {
    textSize: TextProps['size'];
    textRole: TextProps['role'];
}) => (
    <Text size={textSize} role={textRole}>
        Чтобы включить отображение избранного, перейдите в раздел{' '}
        <Link
            data-testid={constructorTestIds.pageHideDescriptionConstructorThemeLink}
            href={constructorRoute.getUrl({
                searchParams: {
                    tab: 'theme',
                },
            })}
        >
            Тема&nbsp;→ Дополнительные возможности
        </Link>
        .
    </Text>
);

const LoyaltyPublishedDescription = ({
    textSize,
    textRole,
}: {
    textSize: TextProps['size'];
    textRole: TextProps['role'];
}) => (
    <Text size={textSize} role={textRole}>
        Страница видна, потому что подключена программа лояльности. Управлять настройками можно в разделе{' '}
        <Link
            data-testid={constructorTestIds.pageHideDescriptionIntegrationsRetailCrmLink}
            target="_blank"
            rel="noreferrer"
            href={settingsIntegrationsRoute.getUrl({
                searchParams: SETTINGS_INTEGRATIONS_SEARCH_PARAMS_CONNECTED,
                hash: SCROLL_TO_ELEMENTS_MAP.settingsIntegrationsRetailCRM,
            })}
        >
            Настройки&nbsp;→ Интеграции&nbsp;→ RetailCRM
        </Link>{' '}
        в личном кабинете.
    </Text>
);

const LoyaltyHiddenDescription = ({
    textSize,
    textRole,
}: {
    textSize: TextProps['size'];
    textRole: TextProps['role'];
}) => (
    <Text size={textSize} role={textRole}>
        Страница будет видна, только если подключена программа лояльности. Вы можете настроить её в разделе{' '}
        <Link
            data-testid={constructorTestIds.pageHideDescriptionIntegrationsRootLink}
            target="_blank"
            rel="noreferrer"
            href={settingsIntegrationsRoute.getUrl({
                searchParams: SETTINGS_INTEGRATIONS_SEARCH_PARAMS_CONNECTED,
                hash: SCROLL_TO_ELEMENTS_MAP.settingsIntegrationsRetailCRM,
            })}
        >
            Настройки&nbsp;→ Интеграции
        </Link>{' '}
        в личном кабинете.
    </Text>
);

const GiftcardPublishedDescription = ({
    textSize,
    textRole,
}: {
    textSize: TextProps['size'];
    textRole: TextProps['role'];
}) => (
    <Text size={textSize} role={textRole}>
        Страница отображается, потому что в разделе{' '}
        <Link
            data-testid={constructorTestIds.pageHideDescriptionPromoGiftcardLink}
            target="_blank"
            rel="noreferrer"
            href={promoactionsRoute.getUrl({
                searchParams: {
                    'active-tab': 'giftcard',
                },
            })}
        >
            Акции&nbsp;→ Подарочный сертификат
        </Link>{' '}
        личного кабинета настроены подарочные карты.
    </Text>
);

const GiftcardHiddenDescription = ({
    textSize,
    textRole,
}: {
    textSize: TextProps['size'];
    textRole: TextProps['role'];
}) => (
    <Text size={textSize} role={textRole}>
        Чтобы страница была видна, создайте подарочные карты. Это можно сделать в разделе{' '}
        <Link
            data-testid={constructorTestIds.pageHideDescriptionPromoGiftcardLink}
            target="_blank"
            rel="noreferrer"
            href={promoactionsRoute.getUrl({
                searchParams: {
                    'active-tab': 'giftcard',
                },
            })}
        >
            Акции&nbsp;→ Подарочный сертификат
        </Link>{' '}
        в личном кабинете.
    </Text>
);

const ProductBundleDescription = ({
    textSize,
    textRole,
}: {
    textSize: TextProps['size'];
    textRole: TextProps['role'];
}) => (
    <Text size={textSize} role={textRole}>
        Настройте в разделе{' '}
        <Link
            data-testid={constructorTestIds.pageHideDescriptionPromoBundlesLink}
            target="_blank"
            rel="noreferrer"
            href={promoactionsRoute.getUrl({
                searchParams: {
                    'active-tab': 'bundles',
                },
            })}
        >
            Акции&nbsp;→ Наборы
        </Link>{' '}
        в личном кабинете.
    </Text>
);

const SubscriptionPublishedDescription = ({
    textSize,
    textRole,
}: {
    textSize: TextProps['size'];
    textRole: TextProps['role'];
}) => (
    <Text size={textSize} role={textRole}>
        Сейчас страница отображается. Управлять доступностью можно в разделе{' '}
        <Link
            data-testid={constructorTestIds.pageHideDescriptionEmailNotificationsLink}
            target="_blank"
            rel="noreferrer"
            href={settingsEmailNotificationsRoute.getUrl()}
        >
            Настройки&nbsp;→ Почтовые уведомления
        </Link>
        .
    </Text>
);

const SubscriptionHiddenDescription = ({
    textSize,
    textRole,
}: {
    textSize: TextProps['size'];
    textRole: TextProps['role'];
}) => (
    <Text size={textSize} role={textRole}>
        Чтобы страница была видна, включите её в разделе{' '}
        <Link
            data-testid={constructorTestIds.pageHideDescriptionEmailNotificationsLink}
            target="_blank"
            rel="noreferrer"
            href={settingsEmailNotificationsRoute.getUrl()}
        >
            Настройки&nbsp;→ Почтовые уведомления
        </Link>{' '}
        в личном кабинете.
    </Text>
);

const getConstructorPageHideDescriptionContent = ({
    alias,
    status,
    textSize,
    textRole,
}: {
    alias: Page['alias'];
    status?: Page['status'];
    textSize: TextProps['size'];
    textRole: TextProps['role'];
}): ReactNode => {
    switch (alias) {
        case CONSTRUCTOR_FAVOURITE_PRODUCTS_ALIAS:
            if (status === 'published') {
                return <FavouritePublishedDescription textSize={textSize} textRole={textRole} />;
            }

            return <FavouriteHiddenDescription textSize={textSize} textRole={textRole} />;
        case CONSTRUCTOR_LOYALTY_OFFER_ALIAS:
        case CONSTRUCTOR_LOYALTY_LANDING_ALIAS:
        case CONSTRUCTOR_ACCOUNT_LOYALTY_ALIAS:
            if (status === 'published') {
                return <LoyaltyPublishedDescription textSize={textSize} textRole={textRole} />;
            }

            return <LoyaltyHiddenDescription textSize={textSize} textRole={textRole} />;
        case CONSTRUCTOR_GIFT_CARD_ALIAS:
        case CONSTRUCTOR_GIFT_CARD_QUESTIONS_ALIAS:
            if (status === 'published') {
                return <GiftcardPublishedDescription textSize={textSize} textRole={textRole} />;
            }

            return <GiftcardHiddenDescription textSize={textSize} textRole={textRole} />;
        case CONSTRUCTOR_PRODUCTS_BUNDLE_ALIAS:
            return <ProductBundleDescription textSize={textSize} textRole={textRole} />;
        case CONSTRUCTOR_SUBSCRIPTION_ALIAS:
            if (status === 'published') {
                return <SubscriptionPublishedDescription textSize={textSize} textRole={textRole} />;
            }

            return <SubscriptionHiddenDescription textSize={textSize} textRole={textRole} />;

        default:
            return null;
    }
};

export const useConstructorPageHideDescription = ({
    alias,
    isSidebar,
    status,
}: {
    alias: Page['alias'];
    isSidebar?: boolean;
    status?: Page['status'];
}): ReactNode => {
    const description = useMemo(() => {
        const textRole = isSidebar ? 'supplementary' : 'main';
        const textSize = isSidebar ? 'xs' : 'm';

        const content = getConstructorPageHideDescriptionContent({ alias, status, textSize, textRole });

        if (!content) {
            return null;
        }

        return (
            <div data-testid={constructorTestIds.pageHideDescription} data-alias={alias ?? ''}>
                {content}
            </div>
        );
    }, [alias, isSidebar, status]);

    return description;
};
